import React from 'react';
import { FiTrendingUp } from 'react-icons/fi';
import LessonTemplate from './components/LessonTemplate';
import QuantLessonPracticeSlot from './components/QuantLessonPracticeSlot';

const ExponentsLesson = () => {
  const sections = [
    {
      id: 'exponent-laws',
      title: 'Laws of Exponents',
      content: {
        introduction:
          'Exponents (or powers) represent repeated multiplication. Understanding exponent laws is crucial for simplifying expressions and solving GRE problems efficiently.',

        concepts: [
          {
            title: 'Product Rule',
            explanation:
              'When multiplying with the same base, add the exponents: §a^m × a^n = a^{m+n}§',
            example: {
              problem: 'Simplify: §2^3 × 2^5§',
              solution: [
                'Same base (2), so add exponents',
                '§2^3 × 2^5 = 2^{3+5} = 2^8§',
                '§2^8 = 256§',
                'Answer: §2^8§ or §256§',
              ],
            },
          },
          {
            title: 'Quotient Rule',
            explanation:
              'When dividing with the same base, subtract the exponents: §a^m ÷ a^n = a^{m-n}§',
            example: {
              problem: 'Simplify: §5^7 ÷ 5^3§',
              solution: [
                'Same base (5), so subtract exponents',
                '§5^7 ÷ 5^3 = 5^{7-3} = 5^4§',
                '§5^4 = 625§',
                'Answer: §5^4§ or §625§',
              ],
            },
          },
          {
            title: 'Power Rule',
            explanation:
              'When raising a power to a power, multiply the exponents: §(a^m)^n = a^{mn}§',
            example: {
              problem: 'Simplify: §(3^2)^4§',
              solution: [
                'Power to a power, so multiply exponents',
                '§(3^2)^4 = 3^{2×4} = 3^8§',
                '§3^8 = 6561§',
                'Answer: §3^8§ or §6561§',
              ],
            },
          },
          {
            title: 'Power of Product Rule',
            explanation:
              'Distribute the exponent to each factor: §(ab)^n = a^n × b^n§',
            example: {
              problem: 'Simplify: §(2×3)^3§',
              solution: [
                'Distribute exponent to each factor',
                '§(2×3)^3 = 2^3 × 3^3§',
                '§= 8 × 27 = 216§',
                'Answer: §216§',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'negative-zero-exponents',
      title: 'Negative & Zero Exponents',
      content: {
        introduction:
          'Negative and zero exponents follow special rules that are frequently tested on the GRE. Understanding these rules is essential for simplifying complex expressions.',

        concepts: [
          {
            title: 'Zero Exponent',
            explanation:
              'Any non-zero number raised to the power of 0 equals 1: §a^0 = 1§ (where §a ≠ 0§)',
            example: {
              problem: 'Evaluate: §7^0 + 100^0 + (xyz)^0§',
              solution: [
                'Any non-zero base to power 0 equals 1',
                '§7^0 = 1§',
                '§100^0 = 1§',
                '§(xyz)^0 = 1§ (assuming §xyz ≠ 0§)',
                '§1 + 1 + 1 = 3§',
              ],
            },
          },
          {
            title: 'Negative Exponents',
            explanation:
              'A negative exponent means reciprocal: §a^{-n} = 1/a^n§',
            example: {
              problem: 'Simplify: §2^{-3}§',
              solution: [
                'Negative exponent = reciprocal',
                '§2^{-3} = 1/2^3§',
                '§= 1/8§',
                'Answer: §1/8§ or §0.125§',
              ],
            },
          },
          {
            title: 'Negative Base with Exponents',
            explanation:
              'Watch for parentheses: §(-a)^n§ vs §-a^n§. Even exponents make negatives positive.',
            example: {
              problem: 'Compare: §(-2)^4§ vs §-2^4§',
              solution: [
                '§(-2)^4 = (-2)×(-2)×(-2)×(-2) = 16§',
                '§-2^4 = -(2^4) = -16§',
                'With parentheses: positive result',
                'Without parentheses: negative result',
                'Key: even exponent + negative base (in parentheses) = positive',
              ],
            },
          },
          {
            title: 'Combining Negative Exponents',
            explanation:
              'Apply the same rules with negative exponents: §a^{-m} × a^{-n} = a^{-m-n}§',
            example: {
              problem: 'Simplify: §3^{-2} × 3^{-5}§',
              solution: [
                'Same base, add exponents (both negative)',
                '§3^{-2} × 3^{-5} = 3^{-2+(-5)} = 3^{-7}§',
                'Convert to positive exponent:',
                '§3^{-7} = 1/3^7 = 1/2187§',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'fractional-exponents',
      title: 'Fractional Exponents & Radicals',
      content: {
        introduction:
          'Fractional exponents represent roots. Understanding the connection between radicals and fractional exponents is key for the GRE.',

        concepts: [
          {
            title: 'Fractional Exponents as Roots',
            explanation:
              'A fractional exponent §1/n§ means nth root: §a^{\\frac{1}{n}} = \\sqrt[n]{a}§',
            example: {
              problem: 'Simplify: §16^{\\frac{1}{2}}§ and §8^{\\frac{1}{3}}§',
              solution: [
                '§16^{\\frac{1}{2}} = \\sqrt{16} = 4§',
                '§8^{\\frac{1}{3}}§ is the cube root of 8, which is §2§',
                'Fractional exponent §1/n§ equals the nth root',
                'Check: §4^2 = 16§ ✓ and §2^3 = 8§ ✓',
              ],
            },
          },
          {
            title: 'General Fractional Exponents',
            prerequisiteNote:
              'Convert the fractional exponent into words: denominator = root, numerator = power.',
            vocabulary: ['numerator', 'denominator', 'root', 'power'],
            explanation:
              '§a^{\\frac{m}{n}}§ is the nth root of §a^m§. You can power then root, or root then power.',
            microExample: {
              problem: 'Evaluate §16^{3/4}§',
              solution: [
                'Denominator 4 means fourth root: §\\sqrt[4]{16}=2§',
                'Numerator 3 means cube: §2^3=8§',
              ],
            },
            greTrap:
              'Root first is often easier because it keeps the numbers smaller.',
            checkQuestion:
              'In §a^{m/n}§, which number tells you the root?',
            example: {
              problem: 'Evaluate: §27^{\\frac{2}{3}}§',
              solution: [
                'Method 1: Root then power',
                '§27^{\\frac{2}{3}} = (\\sqrt[3]{27})^2 = 3^2 = 9§',
                'Method 2: Power then root',
                '§27^{\\frac{2}{3}} = \\sqrt[3]{27^2} = \\sqrt[3]{729} = 9§',
                'Both methods work, choose easier calculation',
              ],
            },
          },
          {
            title: 'Simplifying Radical Expressions',
            prerequisiteNote:
              'Look for perfect-square factors before using a calculator-like approach.',
            vocabulary: ['radical', 'perfect square', 'rationalize', 'simplify'],
            explanation:
              'Use fractional exponents to simplify complex radicals: §\\sqrt{a} \\times \\sqrt{b} = \\sqrt{ab}§',
            microExample: {
              problem: 'Simplify §\\sqrt{18}§',
              solution: [
                'Factor: §18 = 9 × 2§',
                '§\\sqrt{18}=\\sqrt{9}\\sqrt{2}=3\\sqrt{2}§',
              ],
            },
            greTrap:
              'Do not combine unlike radicals unless the expression permits it. §\\sqrt{a}+\\sqrt{b}§ is not §\\sqrt{a+b}§.',
            checkQuestion:
              'What perfect-square factor can you pull out of §\\sqrt{72}§?',
            example: {
              problem: 'Simplify: §\\sqrt{50}§',
              solution: [
                'Factor into perfect square and remainder',
                '§\\sqrt{50} = \\sqrt{25 \\times 2}§',
                '§= \\sqrt{25} \\times \\sqrt{2}§',
                '§= 5\\sqrt{2}§',
              ],
            },
          },
          {
            title: 'Combining Fractional Exponents',
            explanation:
              'Apply exponent rules to fractional exponents: §a^{\\frac{1}{2}} \\times a^{\\frac{1}{3}} = a^{\\frac{1}{2} + \\frac{1}{3}} = a^{\\frac{5}{6}}§',
            example: {
              problem: 'Simplify: §x^{\\frac{1}{4}} \\times x^{\\frac{3}{4}}§',
              solution: [
                'Same base, add exponents',
                '§x^{\\frac{1}{4}} \\times x^{\\frac{3}{4}} = x^{\\frac{1}{4} + \\frac{3}{4}}§',
                '§= x^{\\frac{4}{4}} = x^1 = x§',
                'Answer: §x§',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'comparing-exponents',
      title: 'Comparing Exponential Expressions',
      content: {
        introduction:
          'The GRE often asks you to compare expressions with different bases or exponents. Learning strategies for these comparisons saves time.',

        concepts: [
          {
            title: 'Same Base, Different Exponents',
            explanation:
              'If base §> 1§: larger exponent gives larger value. If §0 < base < 1§: larger exponent gives smaller value.',
            example: {
              problem: 'Compare: §2^{10}§ vs §2^{15}§',
              solution: [
                'Same base (2), which is > 1',
                'Larger exponent gives larger value',
                '§2^{15} > 2^{10}§',
                'In fact: §2^{15} = 32,768§ vs §2^{10} = 1,024§',
              ],
            },
          },
          {
            title: 'Same Exponent, Different Bases',
            explanation:
              'With positive bases and positive exponents: larger base gives larger value.',
            example: {
              problem: 'Compare: §3^{100}§ vs §4^{100}§',
              solution: [
                'Same exponent (100)',
                'Both bases positive',
                'Larger base (4 > 3) gives larger value',
                '§4^{100} > 3^{100}§',
              ],
            },
          },
          {
            title: 'Converting to Same Base',
            explanation:
              'Express both values with a common base: §9^3 = (3^2)^3 = 3^6§',
            example: {
              problem: 'Compare: §4^5§ vs §16^2§',
              solution: [
                'Express with common base 2:',
                '§4^5 = (2^2)^5 = 2^{10}§',
                '§16^2 = (2^4)^2 = 2^8§',
                'Now same base: §2^{10} > 2^8§',
                'Therefore: §4^5 > 16^2§',
              ],
            },
          },
          {
            title: 'Large Exponent Comparisons',
            explanation:
              'For very large exponents, even small base differences create huge value differences.',
            example: {
              problem: 'Compare: §999^{2000}§ vs §1000^{2000}§',
              solution: [
                'Same exponent (2000)',
                'Base difference: only 1',
                'But with exponent 2000, this is massive',
                '§1000^{2000} = (1000/999)^{2000} × 999^{2000}§',
                '§(1000/999)^{2000}§ is enormous',
                'Therefore: §1000^{2000} >> 999^{2000}§',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'exponential-growth',
      title: 'Exponential Growth & Applications',
      content: {
        introduction:
          'Exponential functions model real-world phenomena like compound interest, population growth, and radioactive decay.',

        concepts: [
          {
            title: 'Compound Interest Formula',
            explanation:
              'Amount after t periods: §A = P(1 + r)^t§ where P is principal, r is rate per period.',
            example: {
              problem:
                'An investment of $1,000 grows at 5% annually. What is it worth after 3 years?',
              solution: [
                'Use §A = P(1 + r)^t§',
                '§P = 1000§, §r = 0.05§, §t = 3§',
                '§A = 1000(1.05)^3§',
                '§= 1000(1.157625)§',
                '§≈ $1,157.63§',
              ],
            },
          },
          {
            title: 'Population Growth',
            explanation:
              'If population multiplies by factor k each period: §P_t = P_0 × k^t§',
            example: {
              problem:
                'A bacteria population doubles every hour. Starting with 100, how many after 5 hours?',
              solution: [
                'Doubling means §k = 2§',
                '§P_5 = 100 × 2^5§',
                '§= 100 × 32§',
                '§= 3,200§ bacteria',
              ],
            },
          },
          {
            title: 'Decay and Half-Life',
            explanation:
              'Exponential decay: §N_t = N_0 \\times (\\frac{1}{2})^{\\frac{t}{h}}§ where h is half-life period.',
            example: {
              problem:
                'A substance has half-life of 10 days. Starting with 800g, how much remains after 30 days?',
              solution: [
                'Number of half-lives: §\\frac{30}{10} = 3§',
                '§N = 800 \\times (\\frac{1}{2})^3§',
                '§= 800 \\times \\frac{1}{8}§',
                '§= 100§ grams',
              ],
            },
          },
          {
            title: 'Comparing Growth Rates',
            explanation:
              'Even small differences in growth rates lead to massive differences over time.',
            example: {
              problem:
                'Investment A grows 6% yearly, Investment B grows 8% yearly. Both start at $10,000. After 20 years, approximate difference?',
              solution: [
                '§A: 10000(1.06)^{20} ≈ 10000(3.2) = $32,000§',
                '§B: 10000(1.08)^{20} ≈ 10000(4.7) = $47,000§',
                'Difference: §≈ $15,000§',
                '2% rate difference → 47% value difference over 20 years!',
              ],
            },
          },
        ],
      },
    },
  ];

  const practiceQuestions = [
    {
      type: 'timed',
      timeLimit: 90,
      question: 'Simplify: §2^5 × 2^3§',
      options: ['§2^8§', '§2^{15}§', '§4^8§', '§2^2§', '§4^{15}§'],
      correct: 0,
      explanation:
        'Same base, add exponents: §2^5 × 2^3 = 2^{5+3} = 2^8 = 256§.',
    },
    {
      type: 'timed',
      timeLimit: 100,
      question: 'What is the value of §5^0 + 3^{-1}§?',
      options: ['§4/3§', '§3/4§', '§0§', '§1§', '§1/3§'],
      correct: 0,
      explanation: '§5^0 = 1§ and §3^{-1} = 1/3§. Sum = §1 + 1/3 = 4/3§.',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question: 'Simplify: §(3^2)^4§',
      options: ['§3^8§', '§3^6§', '§6^8§', '§9^4§', '§3^{16}§'],
      correct: 0,
      explanation:
        'Power to power, multiply exponents: §(3^2)^4 = 3^{2×4} = 3^8§.',
    },
    {
      type: 'timed',
      timeLimit: 110,
      question: 'What is §16^{\\frac{1}{2}}§?',
      options: ['§4§', '§8§', '§2§', '§16§', '§32§'],
      correct: 0,
      explanation: '§16^{\\frac{1}{2}} = \\sqrt{16} = 4§. Check: §4^2 = 16§ ✓',
    },
    {
      type: 'timed',
      timeLimit: 130,
      question: 'Compare: §2^{20}§ vs §10^6§. Which is greater?',
      options: [
        '§2^{20}§ is greater',
        '§10^6§ is greater',
        'They are equal',
        'Cannot be determined',
        'Both equal 1,000,000',
      ],
      correct: 0,
      explanation:
        '§2^{20} = (2^{10})^2 = 1024^2 = 1,048,576§. §10^6 = 1,000,000§. Therefore §2^{20} > 10^6§.',
    },
    {
      type: 'timed',
      timeLimit: 110,
      question: 'Simplify: §(x^5 × x^3)/x^4§',
      options: ['§x^4§', '§x^2§', '§x^{12}§', '§x^{15}§', '§x§'],
      correct: 0,
      explanation:
        'Numerator: §x^5 × x^3 = x^8§. Then §x^8/x^4 = x^{8-4} = x^4§.',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question: 'If §3^x = 27§ and §3^y = 9§, what is §x + y§?',
      options: ['§5§', '§6§', '§4§', '§7§', '§3§'],
      correct: 0,
      explanation:
        '§27 = 3^3§, so §x = 3§. §9 = 3^2§, so §y = 2§. Therefore §x + y = 5§.',
    },
    {
      type: 'untimed',
      question: 'Simplify: §7^{10}/7^4§',
      options: ['§7^6§', '§7^{14}§', '§7^{2.5}§', '§1§', '§7^{40}§'],
      correct: 0,
      explanation:
        'Same base, subtract exponents: §7^{10}/7^4 = 7^{10-4} = 7^6§.',
    },
    {
      type: 'untimed',
      question: 'What is §(-2)^6§?',
      options: ['§64§', '§-64§', '§32§', '§-32§', '§12§'],
      correct: 0,
      explanation:
        '§(-2)^6 = (-2)×(-2)×(-2)×(-2)×(-2)×(-2) = 64§. Even exponent makes it positive.',
    },
    {
      type: 'timed',
      question: 'Evaluate: §27^{\\frac{2}{3}}§',
      options: ['§9§', '§18§', '§6§', '§3§', '§27§'],
      correct: 0,
      explanation:
        '§27^{\\frac{2}{3}}§ = (cube root of 27)² = §3^2 = 9§. Or: cube root of §27^2 = 729§ = §9§.',
    },
    {
      type: 'untimed',
      question: 'Evaluate: §27^{\\frac{2}{3}}§',
      options: ['§9§', '§18§', '§6§', '§3§', '§27§'],
      correct: 0,
      explanation:
        '§27^{\\frac{2}{3}}§ = (cube root of 27)² = §3^2 = 9§. Or: cube root of §27^2 = 729§ = §9§.',
    },
    {
      type: 'untimed',
      question: 'If §2^x = 32§, what is the value of §x§?',
      options: ['§5§', '§4§', '§6§', '§16§', '§64§'],
      correct: 0,
      explanation: '§2^x = 32 = 2^5§, therefore §x = 5§.',
    },
    {
      type: 'timed',
      question:
        'An investment doubles every 5 years. If it starts at $1,000, what is it worth after 15 years?',
      options: ['$8,000', '$6,000', '$4,000', '$2,000', '$16,000'],
      correct: 0,
      explanation:
        '15 years = 3 doubling periods. Value = §1000 × 2^3 = 1000 × 8 = $8,000§.',
    },
    {
      type: 'untimed',
      question:
        'An investment doubles every 5 years. If it starts at $1,000, what is it worth after 15 years?',
      options: ['$8,000', '$6,000', '$4,000', '$2,000', '$16,000'],
      correct: 0,
      explanation:
        '15 years = 3 doubling periods. Value = §1000 × 2^3 = 1000 × 8 = $8,000§.',
    },
    {
      type: 'untimed',
      question: 'Simplify: §(5^{-2})^3§',
      options: ['§5^{-6}§', '§5^{-5}§', '§5^6§', '§5^{-1}§', '§-5^6§'],
      correct: 0,
      explanation:
        'Power to power: multiply exponents. §(5^{-2})^3 = 5^{-2 × 3} = 5^{-6}§.',
    },
    {
      type: 'untimed',
      question: 'What is §\\sqrt{64}§ written with a fractional exponent?',
      options: [
        '§64^{\\frac{1}{2}}§',
        '§64^2§',
        '§64^{-\\frac{1}{2}}§',
        '§64^{\\frac{1}{3}}§',
        '§2^{64}§',
      ],
      correct: 0,
      explanation:
        '§\\sqrt{a} = a^{\\frac{1}{2}}§, so §\\sqrt{64} = 64^{\\frac{1}{2}}§.',
    },
    {
      type: 'untimed',
      question:
        'A bacteria population triples every 2 hours. Starting with 50, how many after 6 hours?',
      options: ['1,350', '450', '150', '900', '2,700'],
      correct: 0,
      explanation:
        '6 hours = 3 tripling periods. Population = §50 × 3^3 = 50 × 27 = 1,350§.',
    },
    {
      type: 'timed',
      question: 'What is §\\sqrt{64}§ written with a fractional exponent?',
      options: [
        '§64^{\\frac{1}{2}}§',
        '§64^2§',
        '§64^{-\\frac{1}{2}}§',
        '§64^{\\frac{1}{3}}§',
        '§2^{64}§',
      ],
      correct: 0,
      explanation:
        '§\\sqrt{a} = a^{\\frac{1}{2}}§, so §\\sqrt{64} = 64^{\\frac{1}{2}}§.',
    },
    {
      type: 'timed',
      question:
        'A bacteria population triples every 2 hours. Starting with 50, how many after 6 hours?',
      options: ['1,350', '450', '150', '900', '2,700'],
      correct: 0,
      explanation:
        '6 hours = 3 tripling periods. Population = §50 × 3^3 = 50 × 27 = 1,350§.',
    },
    {
      type: 'untimed',
      questionType: 'quantitative_comparison',
      question: 'Compare the two quantities.',
      quantityA: '§2^5§',
      quantityB: '§5^2§',
      correct: 0,
      explanation:
        'Compare the two quantities directly. Quantity A is greater.',
    },
    {
      type: 'untimed',
      questionType: 'multiple_choice_multi',
      question: 'Which expressions equal §16§? Select all that apply.',
      options: ['§2^4§', '§4^2§', '§8^2§', '§sqrt(256)§', '§2^3§'],
      correct: [0, 1, 3],
      explanation: '§2^4 = 16§, §4^2 = 16§, and §sqrt(256)=16§.',
    },
    {
      type: 'timed',
      timeLimit: 105,
      questionType: 'numeric_entry',
      question: 'Enter the value of §3^4 / 3^2§.',
      correctAnswer: '9',
      acceptedAnswers: ['9'],
      explanation: 'Use exponent rules: §3^4 / 3^2 = 3^(4-2) = 3^2 = 9§.',
    },
  ];

  const quickReference = [
    '§a^m × a^n = a^{m+n}§',
    '§a^m ÷ a^n = a^{m-n}§',
    '§(a^m)^n = a^{mn}§',
    '§a^0 = 1§',
    '§a^{-n} = 1/a^n§',
    '§a^{\\frac{1}{n}} = \\sqrt[n]{a}§',
    '§a^{m/n}§: denominator = root, numerator = power',
    'Simplify radicals by pulling out perfect-square factors',
  ];

  const getStartedWithProps = {
    title: 'Need help with Exponents?',
    spaceName: 'Quant hacks',
    humanCoachTitle: 'Talk to a GRE coach',
    liwazaAITitle: 'Ask AI',
    communityTitle: 'Ask other students',
    showHumanCoach: true,
    showLiwazaAI: true,
    showCommunity: true,
    aifirstmessage:
      'I need help understanding Exponents and Powers for the GRE Quantitative section',
    aicontext:
      'Master exponent laws, negative and fractional exponents, and exponential growth applications',
  };

  return (
    <LessonTemplate
      lessonId="exponents"
      lessonTitle="Exponents"
      lessonIcon={<FiTrendingUp />}
      lessonDescription="Master exponent laws, negative and fractional exponents, and exponential growth applications"
      sections={sections}
      practiceQuestions={practiceQuestions}
      quickReference={quickReference}
      getStartedWithProps={getStartedWithProps}
      supplementalPracticeSlot={
        <QuantLessonPracticeSlot lessonId="exponents" />
      }
    />
  );
};

export default ExponentsLesson;
